/**
 * Pair Type Detection Utility
 * 
 * Determines the pair_type for players within a team based on their genders.
 * Used for players_session_games table.
 */

/**
 * Determines the pair type based on player genders within a team
 * @param {Array} teamPlayers - Array of player objects for a team
 * @param {number} gameTypeId - The game_type_id to check if it's singles
 * @returns {string|null} The pair type ('Mixed', 'Levels', or null for singles)
 */
export const detectPairType = (teamPlayers, gameTypeId) => {
  // Singles games (game_type_id 4, 5, 7) don't have pair types
  const singlesGameTypes = [4, 5, 7]; // Mens Singles, Womens Singles, Unbalanced Singles
  if (singlesGameTypes.includes(gameTypeId)) {
    console.log('[PAIR_TYPE_DEBUG] Singles game detected, returning null');
    return null;
  }

  // Validate input
  if (!teamPlayers || teamPlayers.length !== 2) {
    console.warn('[PAIR_TYPE_DEBUG] Invalid team data for pair type detection:', teamPlayers);
    return null;
  }

  const [player1, player2] = teamPlayers;

  // Check if both players have gender data
  if (!player1.gender || !player2.gender) {
    console.warn('[PAIR_TYPE_DEBUG] Missing gender data for pair type detection', { 
      player1: { id: player1.id, name: player1.name, gender: player1.gender },
      player2: { id: player2.id, name: player2.name, gender: player2.gender }
    });
    return null;
  }

  // Debug logging removed for cleaner console

  // Normalize gender values to lowercase for consistent comparison
  const gender1 = player1.gender ? player1.gender.toLowerCase() : null;
  const gender2 = player2.gender ? player2.gender.toLowerCase() : null;

  // Determine pair type based on genders
  if (gender1 === 'male' && gender2 === 'male') {
    return 'levels';
  } else if (gender1 === 'female' && gender2 === 'female') {
    return 'levels';
  } else if (
    (gender1 === 'male' && gender2 === 'female') ||
    (gender1 === 'female' && gender2 === 'male')
  ) {
    return 'mixed';
  } else {
    // Handle OTHER gender or other combinations
    console.warn('[PAIR_TYPE_DEBUG] Unexpected gender combination for pair type detection:', gender1, gender2);
    return 'levels'; // Fallback to levels instead of null
  }
};

/**
 * Gets a human-readable description of the pair type
 * @param {string} pairType - The pair type
 * @returns {string} Human-readable description
 */
export const getPairTypeDescription = (pairType) => {
  const descriptions = {
    'mixed': 'Mixed Gender Pair',
    'levels': 'Same Gender Pair',
    null: 'Singles Game'
  };
  
  return descriptions[pairType] || 'Unknown Pair Type';
};

/**
 * Validates if a pair type is valid for the given game type
 * @param {string} pairType - The pair type
 * @param {number} gameTypeId - The game type ID
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidPairTypeForGameType = (pairType, gameTypeId) => {
  const singlesGameTypes = [4, 5, 7]; // Mens Singles, Womens Singles, Unbalanced Singles
  
  if (singlesGameTypes.includes(gameTypeId)) {
    return pairType === null;
  }
  
  return pairType === 'mixed' || pairType === 'levels';
}; 